import { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { getRivhitCustomers, getRivhitItems, getRivhitConfig, createRivhitCustomer, updateRivhitCustomer, getRivhitCustomerSettings, updateRivhitCustomerSettings } from '../api/client';
import { fuzzySearchList } from '../lib/fuzzySearch';
import { localIsraeliPhoneNumber, normalizeIsraeliPhoneNumber } from '../lib/phone';

export interface RivhitCustomer {
  customer_id: number;
  last_name: string;
  first_name: string;
  customer_name?: string;
  email?: string;
  phone?: string;
  street?: string;
  city?: string;
  id_number?: number;
  vat_number?: number;
  price_list_id?: number;
  discount_percent?: number;
  allow_message?: boolean;
  payment_terms_days?: number | null;
  payment_terms_source?: string | null;
  payment_terms_updated_at?: string | null;
}

export interface RivhitItem {
  item_id?: number;
  item_name?: string;
  catalog_number?: string;
  item_group_id?: number;
  item_group_name?: string;
  price?: number;
  cost?: number;
  quantity_type?: string;
  barcode?: string;
  is_active?: boolean;
}

interface RivhitDataContextValue {
  /** Whether a Rivhit API key has been configured in Settings */
  configured: boolean;
  /** True while checking if Rivhit is configured */
  configLoading: boolean;

  customers: RivhitCustomer[];
  customersLoading: boolean;
  customersError: string | null;
  /** ISO timestamp of when the customer cache was last written (from backend) */
  customersUpdatedAt: Date | null;
  /** Client-side fuzzy search across name, phone, email, city */
  searchCustomers: (query: string) => RivhitCustomer[];
  /** Load customer list once when a page actually needs it */
  ensureCustomersLoaded: () => Promise<void>;
  /** Force re-fetch from Rivhit API (bypasses cache) */
  refreshCustomers: () => Promise<void>;
  /** Create a new customer in Rivhit and refresh the list */
  createCustomer: (data: Omit<RivhitCustomer, 'customer_id'> & { last_name: string }) => Promise<number>;
  /** Update editable fields of a Rivhit customer */
  updateCustomer: (customerId: number, data: Partial<Pick<RivhitCustomer, 'last_name' | 'first_name' | 'phone' | 'email' | 'city' | 'street'>>) => Promise<void>;
  /** Update allow_message for a Rivhit customer (persisted in DynamoDB) */
  setCustomerAllowMessage: (customerId: number, allow_message: boolean) => Promise<void>;

  items: RivhitItem[];
  itemsLoading: boolean;
  /** ISO timestamp of when the item cache was last written (from backend) */
  itemsUpdatedAt: Date | null;
  /** Client-side fuzzy search across item name, catalog number, group name */
  searchItems: (query: string) => RivhitItem[];
  /** Load item list once when a page actually needs it */
  ensureItemsLoaded: () => Promise<void>;
  /** Force re-fetch from Rivhit API (bypasses cache) */
  refreshItems: () => Promise<void>;

  /** Re-check config and reload data (call after saving keys in Settings) */
  reloadAfterConfigChange: () => Promise<void>;
}

const RivhitDataContext = createContext<RivhitDataContextValue | null>(null);

/** Helper: compose display name from first + last, falling back to customer_name */
export function rivhitDisplayName(c: RivhitCustomer): string {
  const composed = [c.first_name, c.last_name].filter(Boolean).join(' ');
  return composed || c.customer_name || `#${c.customer_id}`;
}

const AUTO_REFRESH_MS = 4 * 60 * 60 * 1000; // 4 hours — matches backend DynamoDB TTL

export function RivhitDataProvider({ children }: { children: React.ReactNode }) {
  const [configured, setConfigured] = useState(false);
  const [configLoading, setConfigLoading] = useState(true);
  const [customers, setCustomers] = useState<RivhitCustomer[]>([]);
  const [customersLoading, setCustomersLoading] = useState(false);
  const [customersError, setCustomersError] = useState<string | null>(null);
  const [customersUpdatedAt, setCustomersUpdatedAt] = useState<Date | null>(null);
  const [items, setItems] = useState<RivhitItem[]>([]);
  const [itemsLoading, setItemsLoading] = useState(false);
  const [itemsUpdatedAt, setItemsUpdatedAt] = useState<Date | null>(null);
  const fetchedRef = useRef(false);
  const customersRequestRef = useRef<Promise<void> | null>(null);
  const itemsRequestRef = useRef<Promise<void> | null>(null);

  const loadCustomers = useCallback(async (force = false) => {
    if (!force && customersRequestRef.current) return customersRequestRef.current;

    const request = (async () => {
      setCustomersLoading(true);
      setCustomersError(null);
      const t0 = performance.now();
      console.log(`[RivhitData] 👥 Loading Rivhit customers... force=${force}`, { timestamp: new Date().toISOString() });
      try {
        // Fetch customers with pagination
        const allRawCustomers: RivhitCustomer[] = [];
        let offset = 0;
        const pageSize = 5000;
        let firstPageCachedAt: string | null = null;

        while (true) {
          const pageRes = await getRivhitCustomers(force, { offset, limit: pageSize });
          const pageBody = pageRes.data as { data: RivhitCustomer[]; cached_at: string | null; total: number } | null;
          const pageCustomers = pageBody?.data ?? [];
          if (offset === 0) firstPageCachedAt = pageBody?.cached_at ?? null;
          if (!pageCustomers.length) break;
          allRawCustomers.push(...pageCustomers);
          if (pageCustomers.length < pageSize) break;
          offset += pageSize;
        }
        
        const [, settingsRes] = await Promise.all([
          Promise.resolve(),
          getRivhitCustomerSettings().catch(() => ({ data: [] })),
        ]);
        
        const settingsMap = new Map(
          (settingsRes.data ?? []).map((s) => [s.rivhit_customer_id, s])
        );
        const customers = allRawCustomers.map((c) => ({
          ...c,
          allow_message: settingsMap.get(c.customer_id)?.allow_message ?? false,
          payment_terms_days: settingsMap.get(c.customer_id)?.payment_terms_days ?? null,
          payment_terms_source: settingsMap.get(c.customer_id)?.payment_terms_source ?? null,
          payment_terms_updated_at: settingsMap.get(c.customer_id)?.payment_terms_updated_at ?? null,
        }));
        console.log(`[RivhitData] ✅ Loaded ${customers.length} customers in ${(performance.now() - t0).toFixed(1)}ms`, { timestamp: new Date().toISOString() });
        setCustomers(customers);
        setCustomersUpdatedAt(firstPageCachedAt ? new Date(firstPageCachedAt) : new Date());
      } catch (err: any) {
        const msg = err?.response?.data?.detail || err?.message || 'Failed to load customers';
        console.error(`[RivhitData] ❌ Failed to load customers after ${(performance.now() - t0).toFixed(1)}ms:`, err, { timestamp: new Date().toISOString() });
        setCustomersError(msg);
      } finally {
        setCustomersLoading(false);
      }
    })();

    customersRequestRef.current = request;
    try {
      await request;
    } finally {
      customersRequestRef.current = null;
    }
  }, []);

  const loadItems = useCallback(async (force = false) => {
    if (!force && itemsRequestRef.current) return itemsRequestRef.current;

    const request = (async () => {
      setItemsLoading(true);
      const t0 = performance.now();
      console.log(`[RivhitData] 📦 Loading Rivhit items... force=${force}`, { timestamp: new Date().toISOString() });
      try {
        const res = await getRivhitItems(force);
        const resBody = res.data as { data: RivhitItem[]; cached_at: string | null } | null;
        const items = resBody?.data ?? [];
        console.log(`[RivhitData] ✅ Loaded ${items.length} items in ${(performance.now() - t0).toFixed(1)}ms`, { timestamp: new Date().toISOString() });
        setItems(items);
        setItemsUpdatedAt(resBody?.cached_at ? new Date(resBody.cached_at) : new Date());
      } catch (err) {
        console.error(`[RivhitData] ❌ Failed to load items after ${(performance.now() - t0).toFixed(1)}ms:`, err, { timestamp: new Date().toISOString() });
      } finally {
        setItemsLoading(false);
      }
    })();

    itemsRequestRef.current = request;
    try {
      await request;
    } finally {
      itemsRequestRef.current = null;
    }
  }, []);

  // Check if Rivhit API key is configured. Data itself loads lazily per-page.
  useEffect(() => {
    if (fetchedRef.current) return;
    fetchedRef.current = true;

    (async () => {
      try {
        const res = await getRivhitConfig();
        const isConfigured = res.data?.has_token === true || res.data?.has_credentials === true;
        setConfigured(isConfigured);
        if (isConfigured) {
          console.log('[RivhitData] 🔑 Rivhit configured');
        } else {
          console.log('[RivhitData] ⚠️ Rivhit not configured — skipping data load. Set API key in Settings.');
        }
      } catch (err) {
        console.warn('[RivhitData] Could not check Rivhit config:', err);
        setConfigured(false);
      } finally {
        setConfigLoading(false);
      }
    })();
  }, []);

  // Called after saving new Rivhit keys in Settings — re-check and reload
  const reloadAfterConfigChange = useCallback(async () => {
    try {
      const res = await getRivhitConfig();
      const isConfigured = res.data?.has_token === true || res.data?.has_credentials === true;
      setConfigured(isConfigured);
      if (isConfigured) {
        setCustomers([]);
        setItems([]);
      } else {
        setCustomers([]);
        setItems([]);
      }
    } catch {
      setConfigured(false);
    }
  }, []);

  const ensureCustomersLoaded = useCallback(async () => {
    if (!configured || customersLoading) return;
    const stale = customersUpdatedAt && (Date.now() - customersUpdatedAt.getTime()) > AUTO_REFRESH_MS;
    // Guard on whether a load has completed (timestamp set on success), not on
    // count: a tenant with zero customers still has a fresh, valid result and
    // must not be refetched on every call (which otherwise storms the API).
    if (customersUpdatedAt && !stale) return;
    await loadCustomers();
  }, [configured, customers.length, customersLoading, customersUpdatedAt, loadCustomers]);

  const ensureItemsLoaded = useCallback(async () => {
    if (!configured || itemsLoading) return;
    const stale = itemsUpdatedAt && (Date.now() - itemsUpdatedAt.getTime()) > AUTO_REFRESH_MS;
    if (items.length > 0 && !stale) return;
    await loadItems();
  }, [configured, items.length, itemsLoading, itemsUpdatedAt, loadItems]);

  const searchCustomersFn = useCallback(
    (query: string): RivhitCustomer[] => {
      if (!query.trim()) return customers;
      return fuzzySearchList(customers, query, (c) => [
        c.first_name,
        c.last_name,
        c.customer_name,
        rivhitDisplayName(c),
        [c.last_name, c.first_name].filter(Boolean).join(' '),
        c.phone,
        localIsraeliPhoneNumber(c.phone),
        c.email,
        c.city,
        c.street,
        c.id_number != null ? String(c.id_number) : undefined,
      ]);
    },
    [customers],
  );

  const searchItemsFn = useCallback(
    (query: string): RivhitItem[] => {
      if (!query.trim()) return items;
      return fuzzySearchList(items, query, (item) => [
        item.item_name,
        item.catalog_number,
        item.item_group_name,
        item.barcode,
      ]);
    },
    [items],
  );

  const forceRefreshCustomers = useCallback(() => loadCustomers(true), [loadCustomers]);
  const forceRefreshItems = useCallback(() => loadItems(true), [loadItems]);

  const createCustomerFn = useCallback(async (data: Omit<RivhitCustomer, 'customer_id'> & { last_name: string }): Promise<number> => {
    const normalizedData = data.phone ? { ...data, phone: normalizeIsraeliPhoneNumber(data.phone) } : data;
    const res = await createRivhitCustomer(normalizedData);
    const customerId = res.data.customer_id;
    // Optimistically add the new customer to the in-memory list so it is
    // immediately visible and searchable. We deliberately avoid an immediate
    // blocking full-list refetch here: creating a customer invalidates the
    // server cache, so that refetch would fetch the entire list fresh from
    // Rivhit, which can exceed the API gateway timeout and surface in the UI
    // as a "Network Error" that hides the list we just updated.
    const newCustomer: RivhitCustomer = {
      ...normalizedData,
      customer_id: customerId,
      first_name: normalizedData.first_name ?? '',
      allow_message: normalizedData.allow_message ?? false,
    };
    setCustomers((prev) =>
      prev.some((c) => c.customer_id === customerId) ? prev : [newCustomer, ...prev],
    );
    return customerId;
  }, []);

  const setCustomerAllowMessageFn = useCallback(async (customerId: number, allow_message: boolean): Promise<void> => {
    await updateRivhitCustomerSettings(customerId, { allow_message });
    setCustomers((prev) =>
      prev.map((c) => c.customer_id === customerId ? { ...c, allow_message } : c)
    );
  }, []);

  const updateCustomerFn = useCallback(async (
    customerId: number,
    data: Partial<Pick<RivhitCustomer, 'last_name' | 'first_name' | 'phone' | 'email' | 'city' | 'street'>>,
  ): Promise<void> => {
    const res = await updateRivhitCustomer(customerId, data);
    const savedCustomer = res.data;
    setCustomers((prev) =>
      prev.map((c) =>
        c.customer_id === customerId
          ? { ...c, ...savedCustomer, allow_message: c.allow_message }
          : c
      )
    );
  }, []);

  const value = useMemo<RivhitDataContextValue>(
    () => ({
      configured,
      configLoading,
      customers,
      customersLoading,
      customersError,
      customersUpdatedAt,
      searchCustomers: searchCustomersFn,
      ensureCustomersLoaded,
      refreshCustomers: forceRefreshCustomers,
      createCustomer: createCustomerFn,
      updateCustomer: updateCustomerFn,
      setCustomerAllowMessage: setCustomerAllowMessageFn,
      items,
      itemsLoading,
      itemsUpdatedAt,
      searchItems: searchItemsFn,
      ensureItemsLoaded,
      refreshItems: forceRefreshItems,
      reloadAfterConfigChange,
    }),
    [configured, configLoading, customers, customersLoading, customersError, customersUpdatedAt, searchCustomersFn, ensureCustomersLoaded, forceRefreshCustomers, createCustomerFn, updateCustomerFn, setCustomerAllowMessageFn, items, itemsLoading, itemsUpdatedAt, searchItemsFn, ensureItemsLoaded, forceRefreshItems, reloadAfterConfigChange],
  );

  return <RivhitDataContext.Provider value={value}>{children}</RivhitDataContext.Provider>;
}

export function useRivhitData(): RivhitDataContextValue {
  const ctx = useContext(RivhitDataContext);
  if (!ctx) throw new Error('useRivhitData must be used within RivhitDataProvider');
  return ctx;
}
